// The deployment loop (ruled 2026-09-12; re-ruled 2026-09-13): what a member
// has learned, what they have used, and what that is worth. Pure derivations
// over the engine's existing data — nothing here is a new tracking mechanic.
//
// THE TIE-OUT — each term, precisely:
//   익힘 (learned)   = an expression whose arc reached 적용하기 (arcApplyAt
//                      stamped, 0 is not a stamp) · +1 per EXPRESSION
//   들음 (노출)      = a COMPLETED 들었어요 submission (kind 'heard' with
//                      completedAt) · +2 per SUBMISSION
//   실전 사용 (적용) = a COMPLETED 사용했어요 submission — born at the doors
//                      OR certified from a 예약 (both end as kind 'used' with
//                      completedAt; an uncertified 예약 is an intention and
//                      earns nothing) · +3 per SUBMISSION
//   points           = 익힘 × 1 + 들음 × 2 + 실전 사용 × 3
//
// Per SUBMISSION, not per distinct phrase (ruled 2026-09-13, superseding
// each-phrase-once): a submission is a real, public, reviewed act — the
// effort is the guardrail. The book's stamps still key by library uid.

/** The rank ladder — names kept, thresholds re-tuned for points (ruled
 *  2026-09-12: with 21 phrases fully learned + heard + used the ceiling is
 *  시니어 매니저; 디렉터 and 파트너 open as domains are published). */
export const LOOP_LADDER = [
  { lv: 1, name: '스태프', threshold: 0 },
  { lv: 2, name: '어쏘', threshold: 15 },
  { lv: 3, name: '팀장', threshold: 40 },
  { lv: 4, name: '시니어 매니저', threshold: 90 },
  { lv: 5, name: '디렉터', threshold: 180 },
  { lv: 6, name: '파트너', threshold: 320 },
];

export const POINTS = { learned: 1, heard: 2, used: 3 };

/** Rule copy (해요체, no exclamation, no dash). One sentence since
 *  2026-09-13: points count per submission, so the once-per-phrase line
 *  retired with the rule it described. */

const has = (v) => v !== undefined && v !== null && v !== '' && v !== 0 && v !== false;

/** Engine expression ids whose arc reached 적용하기. */
/** Started and not yet finished — what the member is on RIGHT NOW. The
 *  composer's 추천 표현 offers these when they have not finished anything yet,
 *  which on day one is the only personal signal there is (2026-09-16). */
export function learningIds(states) {
  const out = [];
  for (const [id, st] of Object.entries(states || {})) {
    if (!st || has(st.arcApplyAt)) continue;
    if (has(st.arcVideoAt) || has(st.arcLearnAt)) out.push(id);
  }
  return out;
}

export function learnedIds(states) {
  const out = [];
  for (const [id, st] of Object.entries(states || {})) if (st && has(st.arcApplyAt)) out.push(id);
  return out;
}

/** Distinct library uids on COMPLETED records of one kind (used !== false
 *  only). A reservation is kind 'used' before it is certified — completedAt
 *  is what separates a use from an intention, so points never land at
 *  reservation time (found 2026-09-13). */
export function phraseSet(records, kind) {
  const set = new Set();
  for (const r of records || []) {
    if (!r || r.kind !== kind || !r.completedAt) continue;
    for (const e of r.expressions || []) if (e && e.used !== false && e.uid) set.add(String(e.uid));
  }
  return set;
}

/** 적용 · 노출 as COMPLETED submission counts (ruled 2026-09-13): every
 *  certified 사용했어요 — direct or from a 예약 — and every 들었어요 record
 *  counts once; a pending reservation counts nothing. */
export function outcomeCounts(records) {
  const n = (kind) => (records || []).filter((r) => r && r.kind === kind && r.completedAt).length;
  return { used: n('used'), heard: n('heard') };
}

export function standingPoints({ learned = 0, heard = 0, used = 0 } = {}) {
  return learned * POINTS.learned + heard * POINTS.heard + used * POINTS.used;
}

/** @returns {{lv, name, points, next: {lv,name,threshold}|null, remaining: number|null}} */
export function rankForPoints(points) {
  let cur = LOOP_LADDER[0];
  for (const r of LOOP_LADDER) if (points >= r.threshold) cur = r;
  const next = LOOP_LADDER.find((r) => r.threshold > points) || null;
  return { lv: cur.lv, name: cur.name, points, next, remaining: next ? next.threshold - points : null };
}

/** 이/가-style josa by the final syllable's batchim — 어쏘가, 팀장이. */
export function koJosa(word, withBatchim, without) {
  const ch = String(word || '').trim().slice(-1).charCodeAt(0);
  const b = ch >= 0xAC00 && ch <= 0xD7A3 ? (ch - 0xAC00) % 28 > 0 : false;
  return b ? withBatchim : without;
}

/** The remaining line, worded as the smallest single ACTION that clears it
 *  when one action is enough (ruled 2026-09-13): points translated back into
 *  behaviour. Null at the top of the ladder. */
export function nextLine(rank) {
  if (!rank || !rank.next) return null;
  const r = rank.remaining;
  const be = (n) => `${n}${koJosa(n, '이에요', '예요')}`;
  if (r <= POINTS.learned) return `표현 하나만 더 익히면 ${be(rank.next.name)}`;
  if (r <= POINTS.heard) return `실전에서 하나만 더 들으면 ${be(rank.next.name)}`;
  if (r <= POINTS.used) return `표현 하나만 더 쓰면 ${be(rank.next.name)}`;
  return `${rank.next.name}까지 ${r}점 남았어요`;
}

/** The within-rank fill for a progress bar: 0..1 toward the next threshold. */
export function rankFill(rank) {
  if (!rank || !rank.next) return 1;
  const cur = LOOP_LADDER.find((l) => l.lv === rank.lv) || LOOP_LADDER[0];
  const span = rank.next.threshold - cur.threshold;
  return span > 0 ? Math.min(1, Math.max(0, (rank.points - cur.threshold) / span)) : 1;
}

/** The 배운 표현 line: learned / the domain's authored total. */
export function domainProgress(domain, states) {
  const ids = [];
  for (const s of (domain && domain.situations) || []) for (const id of s.expressionIds || []) ids.push(id);
  const learned = new Set(learnedIds(states));
  return { learned: ids.filter((id) => learned.has(id)).length, total: ids.length };
}

/** The book: every released domain, every learned expression, stamped.
 *  Unlearned expressions are NOT listed — the book is what you have. */
export function bookModel({ domains, states, records, expressionOf }) {
  const usedSet = phraseSet(records, 'used');
  const heardSet = phraseSet(records, 'heard');
  const learned = new Set(learnedIds(states));
  const groups = [];
  for (const d of domains || []) {
    const rows = [];
    for (const s of d.situations || []) {
      (s.expressionIds || []).forEach((id, i) => {
        if (!learned.has(id)) return;
        const e = expressionOf(id);
        if (!e) return;
        const uid = e.uid != null ? String(e.uid) : (e.libraryUid != null ? String(e.libraryUid) : null);
        rows.push({
          id, uid, en: e.teach_core || e.expression_en, ko: e.meaning_ko, sit: s.label, sitId: s.id, x: i + 1,
          used: !!(uid && usedSet.has(uid)), heard: !!(uid && heardSet.has(uid)),
          example: e.example_en || '',
        });
      });
    }
    if (rows.length) groups.push({ label: d.label, rows, used: rows.filter((r) => r.used).length });
  }
  return groups;
}
